import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  HomeModernIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  ChevronDownIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  Cog6ToothIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import PublishControl from '../components/PublishControl';
import InviteSettingsPanel from './InviteSettingsPanel';

interface Submission {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  nationality: string;
  invitation_source?: string;
  invitation_source_detail?: string;
  arrival_date: string;
  departure_date: string;
  num_adults: number;
  num_children: number;
  room_type: string;
  special_requests: string;
  reference: string;
  status: 'new' | 'reviewed' | 'contacted' | 'confirmed';
  notes: string;
  created_at: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
  new:       { label: 'New',       color: 'bg-blue-100 text-blue-700',   dot: 'bg-blue-500' },
  reviewed:  { label: 'Reviewed',  color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  contacted: { label: 'Contacted', color: 'bg-golf-orange/10 text-golf-orange', dot: 'bg-golf-orange' },
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
};

function formatDate(d: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function InterestSubmissions() {
  const [publishOpen, setPublishOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'submissions' | 'settings'>('submissions');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<Submission | null>(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState<Submission['status']>('new');
  const [roomLabels, setRoomLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchSubmissions();
    fetchRoomLabels();
  }, []);

  const fetchRoomLabels = async () => {
    const { data } = await supabase
      .from('invitation_accommodation_types')
      .select('id, name');
    if (data) {
      const map: Record<string, string> = {};
      data.forEach(r => { map[r.id] = r.name; });
      setRoomLabels(map);
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('interest_submissions')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSubmissions(data || []);
    } catch (err: any) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (s: Submission) => {
    setSelected(s);
    setEditNotes(s.notes || '');
    setEditStatus(s.status);
  };

  const saveDetail = async () => {
    if (!selected) return;
    setSavingStatus(true);
    try {
      const { error } = await supabase
        .from('interest_submissions')
        .update({ status: editStatus, notes: editNotes, updated_at: new Date().toISOString() })
        .eq('id', selected.id);
      if (error) throw error;
      setSubmissions(prev => prev.map(s => s.id === selected.id ? { ...s, status: editStatus, notes: editNotes } : s));
      setSelected(prev => prev ? { ...prev, status: editStatus, notes: editNotes } : null);
      toast.success('Updated');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingStatus(false);
    }
  };

  const filtered = submissions.filter(s => {
    const q = search.toLowerCase();
    const matchesSearch = !q || `${s.first_name} ${s.last_name} ${s.email} ${s.reference}`.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const counts = {
    all: submissions.length,
    new: submissions.filter(s => s.status === 'new').length,
    reviewed: submissions.filter(s => s.status === 'reviewed').length,
    contacted: submissions.filter(s => s.status === 'contacted').length,
    confirmed: submissions.filter(s => s.status === 'confirmed').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-golf-gray">Invite Form</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage RSVP submissions and invitation settings</p>
        </div>
        <button
          onClick={() => setPublishOpen(o => !o)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors self-start sm:self-auto"
        >
          <span>Publication Settings</span>
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${publishOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {publishOpen && (
        <div>
          <PublishControl pageKey="interest_form" pageTitle="Invite Form" />
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setActiveTab('submissions')}
            className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'submissions' ? 'border-golf-orange text-golf-orange' : 'border-transparent text-gray-500 hover:text-golf-gray'}`}
          >
            <UserGroupIcon className="w-4 h-4" />
            Submissions
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'settings' ? 'border-golf-orange text-golf-orange' : 'border-transparent text-gray-500 hover:text-golf-gray'}`}
          >
            <Cog6ToothIcon className="w-4 h-4" />
            Settings & Accommodation
          </button>
        </div>
      </div>

      {activeTab === 'settings' && <InviteSettingsPanel />}
      {activeTab === 'submissions' && (<>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {(['new', 'reviewed', 'contacted', 'confirmed'] as const).map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s === statusFilter as any ? 'all' : s)}
            className={`bg-white rounded-xl border p-3 text-left transition-all ${statusFilter === s ? 'border-golf-orange ring-1 ring-golf-orange' : 'border-gray-200 hover:border-gray-300'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full ${STATUS_CONFIG[s].dot}`} />
              <span className="text-xs font-medium text-gray-500 capitalize">{s}</span>
            </div>
            <span className="text-2xl font-bold text-golf-gray">{counts[s]}</span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email or reference..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange"
            />
          </div>
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white [color-scheme:light] appearance-none"
            >
              <option value="all">All statuses ({counts.all})</option>
              {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                <option key={v} value={v}>{c.label} ({counts[v as keyof typeof counts]})</option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <ClockIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No submissions found</p>
            {search && <p className="text-sm mt-1">Try adjusting your search</p>}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((s, i) => (
              <motion.button
                key={s.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                onClick={() => openDetail(s)}
                className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-golf-green/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-golf-green text-sm font-bold">
                      {s.first_name[0]}{s.last_name[0]}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-golf-gray text-sm truncate">{s.first_name} {s.last_name}</p>
                    <p className="text-xs text-gray-500 truncate">{s.email}</p>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-6 text-xs text-gray-500 flex-shrink-0">
                  <span className="flex items-center gap-1">
                    <CalendarDaysIcon className="w-3.5 h-3.5" />
                    {formatDate(s.arrival_date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <HomeModernIcon className="w-3.5 h-3.5" />
                    {roomLabels[s.room_type] || s.room_type}
                  </span>
                  <span className="font-mono text-gray-400">{s.reference}</span>
                </div>
                <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[s.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[s.status]?.dot || 'bg-gray-400'}`} />
                  {STATUS_CONFIG[s.status]?.label || s.status}
                </span>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="font-bold text-golf-gray">{selected.first_name} {selected.last_name}</h2>
                <p className="text-xs text-gray-500 font-mono mt-0.5">{selected.reference}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <InfoRow icon={<EnvelopeIcon className="w-4 h-4" />} label="Email" value={selected.email} />
                <InfoRow icon={<PhoneIcon className="w-4 h-4" />} label="Phone" value={selected.phone} />
                {selected.nationality && <InfoRow icon={<GlobeAltIcon className="w-4 h-4" />} label="Nationality" value={selected.nationality} />}
                <InfoRow icon={<CalendarDaysIcon className="w-4 h-4" />} label="Arrival" value={formatDate(selected.arrival_date)} />
                <InfoRow icon={<CalendarDaysIcon className="w-4 h-4" />} label="Departure" value={formatDate(selected.departure_date)} />
                <InfoRow icon={<UserGroupIcon className="w-4 h-4" />} label="Guests" value={`${selected.num_adults} adult${selected.num_adults !== 1 ? 's' : ''}${selected.num_children ? `, ${selected.num_children} child${selected.num_children !== 1 ? 'ren' : ''}` : ''}`} />
                <InfoRow icon={<HomeModernIcon className="w-4 h-4" />} label="Room" value={roomLabels[selected.room_type] || selected.room_type} />
                <InfoRow icon={<ClockIcon className="w-4 h-4" />} label="Submitted" value={formatDate(selected.created_at)} />
                {selected.invitation_source && (
                  <InfoRow
                    icon={<ChatBubbleLeftEllipsisIcon className="w-4 h-4" />}
                    label="Source"
                    value={selected.invitation_source_detail ? `${selected.invitation_source} — ${selected.invitation_source_detail}` : selected.invitation_source}
                  />
                )}
              </div>

              {selected.special_requests && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">Special Requests</p>
                  <p className="text-sm text-gray-700">{selected.special_requests}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
                <select
                  value={editStatus}
                  onChange={e => setEditStatus(e.target.value as Submission['status'])}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white [color-scheme:light]"
                >
                  {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                    <option key={v} value={v}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  <ChatBubbleLeftIcon className="w-3.5 h-3.5 inline mr-1" />
                  Internal Notes
                </label>
                <textarea
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                  rows={3}
                  placeholder="Add internal notes..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange resize-none"
                />
              </div>

              <button
                onClick={saveDetail}
                disabled={savingStatus}
                className="w-full py-2.5 bg-golf-orange text-white text-sm font-semibold rounded-xl hover:bg-golf-orange/90 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {savingStatus ? (
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <CheckCircleIcon className="w-4 h-4" />
                )}
                Save Changes
              </button>
            </div>
          </motion.div>
        </div>
      )}
      </>)}
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-400 mt-0.5 flex-shrink-0">{icon}</span>
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm font-medium text-golf-gray">{value}</p>
      </div>
    </div>
  );
}
